import { CancelTokenSource } from "axios";

export interface RootState {
	[x: string]: any; // hacky,
	// solves problem described here
	// @ref https://forum.vuejs.org/t/vuex-submodules-with-typescript/40903/4
	appInitialised: boolean;
	dataError: boolean;
	User: User;
	UserGroups: UserGroup[];
	keyboardShortcuts: KeyboardShortcut[];
	timeZoneAbbr: string;
	windowId: string;
	localUserData: User;
	version: string;
	featuresList: FeaturesList;
	timeZones: TimeZone[];
	loading: boolean;
	loginPreferences: LoginPreferences;
	ulTypes: UlType[];
}

export interface User {
	loggedIn: boolean;
	fullName: string;
	userId: number;
	userGroupId: number;
	error: string;
	isFullUser: boolean;
	userPermissions: UserPermissions;
	useImperialMeasurements: boolean;
	mapType?: string;
	mapKey?: string;
	hideLabels?: boolean;
	requireMapPin?: boolean;
	tourNoteRequired?: boolean;
	alarmScreenTimeoutSeconds?: number | null;
	alarmAgeWarningSeconds?: number | null;
	canAddIndividualActionsOverride?: boolean;
	allowEmptyAddressesOnAreas: boolean;
	permittedFileTypes: string;
	zoomLevelToLoadMapLayers: number;
	collapseParentCategoryByDefault?: boolean;
	dbFederationEnabled: boolean;
	mediaMatrixState: MatrixState;
	dynamicLinkConfig: DynamicLinkConfig;
	mediaMatrixConfig: MediaMatrixConfig;
	quickControlMaxCameraRecordDuration: number;
	requireReasonUpdatingClearanceOnReaderGroups: boolean;
	additionalActionsOnForceRestoredAlarms: boolean;
	quickControlPrefs: QuickControlPreferences;
}

export interface UserPermissions {
	canManageContactRoles: boolean;
	canEditSettings: boolean;
	canHandleAlarms: boolean;
	canInteractHighPriorityBanner: boolean;
	canViewReports: boolean;
	canAddIndividualActions: boolean;
	canEditUserPermissions: boolean;
	canEditUserGroups: boolean;
	canViewSiteSetup: boolean;
	canEditSiteSetup: boolean;
	isAdmin: boolean;
	canRaiseActivityLog: boolean;
	canToggleLogVisibility: boolean;
	canEditOwnActivityLog: boolean;
	canEditOtherActivityLogs: boolean;
	canDisarmSites: boolean;
	canDisarmSitesExtended: boolean;
	canSetResponseToMasked: boolean;
	canSetSiteToOnTest: boolean;
	canSetSiteToAuditMode: boolean;
	canViewEvents: boolean;
	canAccessQuickControl: boolean;
	canViewMediaMatrisControls: boolean;
	canAccessManualRaise: boolean;
	canAccessManualTour: boolean;
	canViewSitRep: boolean;
	canUseMobile: boolean;
	canViewMobileRaise: boolean;
	canEditMobileRaise: boolean;
	canDownloadEventSearchSitRep: boolean;
	canDownloadEventSearchMedia: boolean;
	canViewLicenseDetails: boolean;
	canPerformVideoReport: boolean;
	canPerformVideoReportUpload: boolean;
	canViewActivityLogManagement: boolean;
	canProcessActiveAlarms: boolean;
	canDownloadSingleClip: boolean;
	canViewExternalMapLayers: boolean;
	canEditExternalMapLayers: boolean;
	canEditInsightsTitle: boolean;
	canExtendAlerts: boolean;
	canEditMaps: boolean;
	canEditSystemSettings: boolean;
	canEditEventCategorySetup: boolean;
	canEditGuardTours: boolean;
	canViewGuardTours: boolean;
	canEnableDisableUsers: boolean;
	canAssignGuardTour: boolean;
	canConductGuardTour: boolean;
	canClaimFromMobileRaise: boolean;
	canMobileDispatch: boolean;
	canAccessQuickControlEvents: boolean;
	canAccessVideowall: boolean;
	canViewSystemEvents: boolean;
	canViewSiteArmedState: boolean;
	canEndMultipleAlarms: boolean;
	canChangeGuardType: boolean;
	canPerformMatrixSnapshot: boolean;
	canViewAttachedMedia: boolean;
	canTriggerTestAlarm: boolean;
	canViewCCureLockdown: boolean;
	canEditCCureLockdown: boolean;
	canPromoteSelfToIC: boolean;
	canPerformQuickControlWithoutFootage: boolean;
	canViewCameras: boolean;
	canViewExternalMenuLinks: boolean;
	canEditExternalMenuLinks: boolean;
	canClaimEvents: boolean;
	canSelectConfigByCheckbox: boolean;
	canDispatchAssessor: boolean;
	canCreateCustomSQLReports: boolean;
	canShareReports: boolean;
	canShareReportsPublicly: boolean;
	canManualRaiseRegionally: boolean;
	canMaskPointsRegionally: boolean;
	canArmDisarmRegionally: boolean;
	canViewActionPlans: boolean;
	canEditActionPlans: boolean;
	canViewDispatchSetup: boolean;
	canEditDispatchSetup: boolean;
	canQuickControlRegions: boolean;
	canResetCCureRegions: boolean;
	canUseRegionsInViews: boolean;
	canManageDomains: boolean;
	canEditDomainGroups: boolean;
	canViewDomainGroups: boolean;
	canViewBadgeActivity: boolean;
	canEditLockUnlock: boolean;
	canSetDuressEventType: boolean;
	canRaiseDuress: boolean;
}

export interface UserGroup {
	groupID: number;
	parentGroupID: number;
	groupTypeID: number;
	title: string;
	armed: boolean;
	disabledAt: string;
	mobileAppEnabled: boolean;
	licenseTypeID: number;
	latLong?: string;
	address?: string;
}

export interface KeyboardShortcut {
	keys: string;
	action: string;
	featureFlag?: string;
}

export interface LegendKey {
	icon: string;
	details: string;
}

export interface RoutingGroup {
	routingGroupID: number;
	groupID: number;
	title: string;
	isDefault: boolean;
	isActive: boolean;
	unattendedEscalateTo: number;
	canEscalateTo: string;
	colourCode: string;
}

export interface EventSearchFeatures {
	PreserveEvents: boolean | undefined | null;
	SupplementalNotes: boolean | undefined | null;
	AlarmTag: boolean | undefined | null;
	DownloadEvent: boolean | undefined | null;
	DownloadSingleClip: boolean | undefined | null;
}

export interface MediaMatrixFeatures {
	AreaTree: boolean | undefined | null;
	RelayControl: boolean | undefined | null;
	Tours: boolean | undefined | null;
	DeviceAudit: boolean | undefined | null;
	LinkedCameras: boolean | undefined | null;
	Clear: boolean | undefined | null;
	HideMenu: boolean | undefined | null;
	SnapShot: boolean | undefined | null;
	Media: boolean | undefined | null;
}

export interface DispatchFeatures {
	MultiDispatch: boolean | undefined | null;
}

export interface SiteMonitorFeatures {
	Map: boolean | undefined | null;
	EventSharing: boolean | undefined | null;
	SitRep: boolean | undefined | null;
	Raise: boolean | undefined | null;
	SiteDetails: boolean | undefined | null;
	NoteAttachments: boolean | undefined | null;
	Park: boolean | undefined | null;
	LinkedCameras: boolean | undefined | null;
	EventFlagging: boolean | undefined | null;
	AcknowledgeAll: boolean | undefined | null;
	CameraSearch: boolean | undefined | null;
	RequireAreaNotesAcknowledgement: boolean | undefined | null;
}

export interface EventQueueFeatures {
	Map: boolean | undefined | null;
	ManualRaise: ManualRaiseFeatures | undefined | null;
	ManualTour: boolean | undefined | null;
	InProcessing: boolean | undefined | null;
	Parked: boolean | undefined | null;
	Patrol: boolean | undefined | null;
	DisableProcessingActiveAlarms: boolean | undefined | null;
	EndMultipleEvents: boolean | undefined | null;
}

export interface ManualRaiseFeatures {
	RequireNote: boolean | undefined | null;
	DisableOnArea: boolean | undefined | null;
}

export interface ActionPlanFeatures {
	AssignActions: boolean | undefined | null;
}

export interface AlarmPointsFeatures {
	OnTest: boolean | undefined | null;
	Masking: boolean | undefined | null;
	AuditMode: boolean | undefined | null;
	AlarmHistory: boolean | undefined | null;
}

export interface AlarmFeatures {
	MediaMatrix: MediaMatrixFeatures | undefined | null;
	Dispatch: DispatchFeatures | undefined | null;
	EventSearch: EventSearchFeatures | undefined | null;
	SiteMonitor: SiteMonitorFeatures | undefined | null;
	EventQueue: EventQueueFeatures | undefined | null;
	QuickControl: QuickControlFeatures | undefined | null;
	AlarmQueueFilters: boolean | undefined | null;
	ActionPlans: ActionPlanFeatures | undefined | null;
	AlarmPoints: AlarmPointsFeatures | undefined | null;
	AutoPassAlarmOnLeave: boolean | undefined | null;
	Maps: MapsFeatures | undefined | null;
}

export interface QuickControlFeatures {
	LogDoorAction: boolean | undefined | null;
	Cameras: boolean | undefined | null;
}

export interface MapsFeatures {
	ToggleSiteMonitor: boolean | undefined | null;
	ToggleEventQueue: boolean | undefined | null;
}

export interface ActionsFeatures {
	Choices: boolean | undefined | null;
	CallContact: boolean | undefined | null;
	MassNotification: boolean | undefined | null;
	SitRep: boolean | undefined | null;
	CCure: boolean | undefined | null;
	ViewAudit: boolean | undefined | null;
	DynamicDataList: boolean | undefined | null;
	CollapseCompletedActions: boolean | undefined | null;
	Control: boolean | undefined | null;
	RaiseToCaseManagement: boolean | undefined | null;
	EventOutcome: boolean | undefined | null;
}

export interface CCureUIFeatures {
	LogALL: boolean | undefined | null;
	LogDoorAction: boolean | undefined | null;
	LogSingle: boolean | undefined | null;
	Lockdown: LockdownFeatures | undefined | null;
	PostProcessingQueue: boolean | undefined | null;
}

export interface LockdownFeatures {
	Reason: boolean | undefined | null;
}

export interface CCureFeatures {
	UI: CCureUIFeatures | undefined | null;
}

export interface IntegrationFeatures {
	Lenel: boolean | undefined | null;
	CCure: CCureFeatures | undefined | null;
	Genetec: boolean | undefined | null;
}

export interface MobileFeatures {
	CheckIn: boolean | undefined | null;
	Dispatch: boolean | undefined | null;
	ManualRaise: boolean | undefined | null;
	Tracking: boolean | undefined | null;
	Views: boolean | undefined | null;
	GuardCovering: boolean | undefined | null;
	GuardTours: boolean | undefined | null;
	EventQueue: boolean | undefined | null;
	Duress: boolean | undefined | null;
}

export interface CaseManagementFeatures {
	RaiseToCaseManagement: boolean | undefined | null;
}

export interface EventTypeSetupFeatures {
	Geofence?: boolean | undefined | null;
}

export interface InsightsFeatures {
	CustomSQLReports: boolean | undefined | null;
	ShowAllResults: boolean | undefined | null;
	ShowAdvancedDateFilter: boolean | undefined | null;
	ReportFavorites: boolean | undefined | null;
	ReportHistory: boolean | undefined | null;
}

export interface DomainsFeatures {
	ActiveDirectory: boolean | undefined | null;
}

export interface FeaturesList {
	Alarms: AlarmFeatures | undefined | null;
	Actions: ActionsFeatures | undefined | null;
	Integration: IntegrationFeatures | undefined | null;
	ActivityLog: boolean | undefined | null;
	Insights: InsightsFeatures | undefined | null;
	Mobile: MobileFeatures | undefined | null;
	View: boolean | undefined | null;
	Users: boolean | undefined | null;
	EventCategorySetup: boolean | undefined | null;
	EventTypeSetup: EventTypeSetupFeatures | undefined | null;
	VideoReporting: boolean | undefined | null;
	CaseManagement: CaseManagementFeatures | undefined | null;
	Dispatch: DispatchGuardFeatures | undefined | null;
	Domains: DomainsFeatures | undefined | null;
	MapSetup: MapSetupFeatures | undefined | null;
}

export interface MapSetupFeatures {
	MapConversion: boolean | undefined | null;
	MapManagement: boolean | undefined | null;
}

export interface DispatchGuardFeatures {
	ChangeGuardType: boolean | undefined | null;
}

export enum MatrixState {
	closed = 0,
	initializing = 1,
	open = 2
}

export interface TimeZone {
	timeZoneID: number;
	id: string; // this is the name of the timezone
}

export interface UlType {
	ulTypeId: number;
	title: string;
}

export interface DynamicLinkConfig {
	testPattern: string;
	linkFormatter: string;
}

export interface VueBootstrapField {
	key: string;
	label: string;
	sortable?: boolean;
	sortDirection?: string;
	formatter?: any;
	class?: string;
	isActive?: boolean;
	filterByFormatted?: boolean;
	sortByFormatted?: boolean;
	headerTitle?: string;
	visible?: boolean;
	searchable?: boolean;
	dataType?: string;
	thStyle?: Object;
	thClass?: string;
	editable?: boolean;
}

export interface VueBootstrapRadioField {
	text: string;
	value: string;
}

export interface GroupAndTermQuery {
	groupId: number;
	searchTerm: string;
}

export interface MediaMatrixConfig {
	videoToursExpanded: boolean;
	clipsExpanded: boolean;
	camerasExpanded: boolean;
	controlsExpanded: boolean;
	areasExpanded: boolean;
	filesExpanded: boolean;
	audioExpanded: boolean;
}

export interface Region {
	regionId: number;
	url: string;
	title: string;
	regionUserId: number;
	connected: boolean;
	friendlyName: string;
	active: boolean;
}

export enum LoginStatus {
	Success = 0,
	InvalidPassword = 1,
	LockedOut = 2,
	DisabledUser = 3,
	Error = 4,
	Removed = 5,
	PasswordExpired = 6
}

export interface LoginResult {
	status: LoginStatus;
	loginFullname: string;
	loginUserID: number;
	loginUserGroupID: number;
	error: string;
	permissions: UserPermissions;
	useImperialMeasurements: boolean;
	mapType: string;
	hideLabels: boolean;
	requireMapPin: boolean;
	canAddIndividualActionsOverride: boolean;
	allowEmptyAddressesOnAreas: boolean;
	permittedFileTypes: string;
	zoomLevelToLoadMapLayers: number;
	collapseParentCategoryByDefault: boolean;
	alarmScreenTimeoutSeconds: number | null;
	alarmAgeWarningSeconds: number | null;
	dbFederationEnabled: boolean;
	tourNoteRequired: boolean;
	featuresList: FeaturesList;
	redirectRoute: string;
	dynamicLinkConfig: DynamicLinkConfig;
	mediaMatrixConfig: MediaMatrixConfig;
	isFullUser?: boolean;
	mapKey?: string;
	quickControlMaxCameraRecordDuration: number;
	requireReasonUpdatingClearanceOnReaderGroups: boolean;
	additionalActionsOnForceRestoredAlarms: boolean;
	quickControlWarning: string;
	quickControlRegex: string;
	landingPage?: string[];
}

export interface SingleEventLoginResult {
	eventID: number;
	status: LoginStatus;
	error: string;
	permissions: { [permission: string]: boolean };
	isFullUser?: boolean;
}

export interface RegionLoginResult {
	loginResult: LoginResult;
	xsrfToken: string;
}

export interface PaginatedQueryParams {
	page: number;
	pageSize: number;
}

export interface PaginatedSearchQueryParams extends PaginatedQueryParams {
	searchTerm?: string;
}

export interface CancellableQuery<T> {
	params: T;
	cancelTokenSource: CancelTokenSource;
}

export interface LoginPreferences {
	domainLoginSelectEnabled: boolean;
	domainLoginAllowNoDomain: boolean;
	ssoProviderSettings: SsoProviderSettings;
	ssoLoginButtonString: string | null;
}

export interface SsoProviderSettings {
	providerDomain: string;
	providerClientId: string;
	redirectUrl: string | null;
	ssoEnabled: boolean;
	authorizationEndpoint: string;
	ssoAutoRedirect: boolean;
	providerType: number;
}

export interface QuickControlPreferences {
	warning: string;
	regex: string;
}

export interface UserGroupTable {
	userGroupId: number;
	title: string;
	groupId: number;
}

export interface OktaRequestResponse {
	successful: boolean;
	requestId: string | null;
	url: string | null;
	error: string;
}

export interface OktaRequestSuccessfulResponse {
	successful: boolean;
	username: string | null;
	error: string;
}

export enum AlarmSoundType {
	Alarm = "alarm.wav",
	SystemEvent = "alarmTrouble.wav",
	Patrol = "patrol.wav"
}

export enum enumMessageType {
	confirmation,
	warning,
	error,
}

export interface ConfirmationMessage {
	messageType: enumMessageType,
	text: string
}

export interface ResponseSearchRequest {
	GroupID: number;
	RegionURL?: string;
	Query?: string;
}